import { ChangeEvent, useMemo } from 'react';

import { Card, Table, TableBody, TableContainer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';


import { useTranslate } from 'src/locales';
import { PageOptions, PaginatedResponse } from 'src/services/api.types';
import { IMeeting } from 'src/services/meetings/meetings.types';

import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';


import MeetingTableRow from './meeting-table-row';
import MeetingTableToolbar from './meeting-table-toolbar';

type Props = {
  meetings?: PaginatedResponse<IMeeting>;
  pageOptions: PageOptions;
  isLoading: boolean;
  onFilters?: (value: string) => void;
  onAddMeeting?: () => void;
  onChangePage: (_event: any, pageNumber: number) => void;
  onChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export default function MeetingTable({
  meetings,
  pageOptions,
  isLoading = false,
  onFilters,
  onAddMeeting,
  onChangePage,
  onChangeRowsPerPage,
}: Props) {

  const table = useTable();

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const denseHeight = table.dense ? 56 : 56 + 20;

  const tableLabels = useMemo(
    () => [
      { id: 'title', label: t('meetings.table.labels.title') },
      { id: 'date', label: t('meetings.table.labels.date') },
      { id: 'duration', label: t('meetings.table.labels.duration') },
      { id: 'platform', label: t('meetings.table.labels.platform') },
      { id: 'status', label: t('meetings.table.labels.status') },
      { id: '' },
    ],
    [t]
  );

  const notFound = !meetings?.count;


  return (
    <Card sx={{ pt: { xs: 2, sm: 'unset' } }}>

      {
        isLoading ? (
          <TableSkeleton />
        )
          :
          <>
            <MeetingTableToolbar
              table={table}
              onFilters={onFilters}
              onAddMeeting={onAddMeeting}
            />

            <TableContainer sx={{ overflow: 'unset' }}>
              <Scrollbar>
                <Table size={table.dense ? 'small' : 'medium'}>

                  {
                    !isMobile && (
                      <TableHeadCustom
                        headLabel={tableLabels}
                        rowCount={meetings?.results.length ?? 0}
                        numSelected={table.selected.length}
                        onSelectAllRows={(checked) =>
                          table.onSelectAllRows(
                            checked,
                            (meetings?.results ?? []).map((row) => row.id) as string[]
                          )
                        }
                      />
                    )
                  }

                  <TableBody>
                    {meetings?.results?.map((row) =>
                      <MeetingTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                      />
                    )}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, meetings?.count ?? 0)}
                    />

                    <TableNoData notFound={notFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={meetings?.count ?? 0}
              page={pageOptions.page}
              rowsPerPage={pageOptions.per_page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </>
      }

    </Card>
  );
}